/**
 * Async WebSocket actions for DigitalEvent
 */
import DigitalEventStore from "../../index";
import {
  INVITE_STATUSES,
  ROUTES,
  WSS_ACTION_TYPES,
  WSS_MESSAGE_TYPES,
  ANNOUNCEMENT_TYPES,
  MESSAGE_STATUS
} from "@/helpers/constants";
import router from "@/router/DigitalEventRouter";
import Sarus from "@anephenix/sarus";
import { getPage, getTimezoneOffset } from "@/helpers/configHelpers";
import dayjs from "dayjs";
import { getUUID } from "@/helpers/helpers";

/**
 * Common logger function
 */
function logger() {
  if (DigitalEventStore.getters["event/webSocketConfig"].loggingOn) {
    Array.prototype.slice.call(arguments).forEach(arg => {
      // eslint-disable-next-line no-console
      console.log(arg);
    });
  }
}

export default {
  /**
   * Create WebSocket connection
   * @param state
   * @param commit
   * @param dispatch
   * @param getters
   * @param onOpen
   */
  createWebSocketConnection({ state, commit, dispatch, rootState, rootGetters }, onOpen) {
    if (state.socket || !rootState.users.user) {
      return;
    }

    // Collecting params for connecting to WebSocket
    const url = rootGetters["event/webSocketConfig"].url;
    const sessionUUID = rootState.event.config.sessionUUID;
    const page = getPage();
    const tz = dayjs.tz.guess();
    const tzOffset = getTimezoneOffset();

    let roomUUID = rootGetters["room/activeRoom"].uuid;
    let tableUUID = rootGetters["tables/userTable"]?.uuid;
    let assignmentId = rootGetters["users/userAssignment"].id;

    // TODO Fixme Replace event_uuid with DigitalEvent.uuid

    /*eslint max-len: ["error", { "code": 400 }]*/
    let wsConnection = `${url}?Auth=${sessionUUID}&PAGE=${page}&TZ=${tz}&TZO=${tzOffset}&room_uuid=${roomUUID}&table_uuid=${tableUUID}&assignment_id=${assignmentId}`;
    let socket = new Sarus({
      url: wsConnection,
      eventListeners: {
        open: [
          () => {
            logger("<<:", "open");
            dispatch("setPingHandler");

            if (onOpen) {
              dispatch(onOpen.name, onOpen.params);
            }
          }
        ],
        error: [
          error => {
            logger("<< error:", error);
            dispatch("logout", {}, { root: true });
            router.push({ name: ROUTES.DIGITAL_EVENT_AUTH_LOGIN }).catch(() => {});
          }
        ],
        message: [],
        close: []
      },
      reconnectAutomatically: true,
      retryConnectionDelay: true,
      storageType: "local",
      storageKey: "ev-messages"
    });

    socket.on("message", event => {
      logger("onmessage event:", event);

      let message = JSON.parse(event.data);
      logger("onmessage message:", message);

      if (!message) {
        // eslint-disable-next-line no-console
        console.warn("Invalid message received!");
        return;
      }

      if (message.messageType === WSS_MESSAGE_TYPES.SYSTEM) {
        /**
         * Process system ws messages
         */
        switch (message.messageActionType) {
          case WSS_ACTION_TYPES.VIDEO_CHAT_UPDATED:
            commit("videoChat/updateVideoChat", message.messageBody, { root: true });
            break;
        }
      } else if (message.messageType === WSS_MESSAGE_TYPES.TABLE_CREATION) {
        /**
         * Dynamic table creation notify
         */
        dispatch("tables/addTable", { tableUUID: message.messageBody }, { root: true });
      } else if (message.messageType === WSS_MESSAGE_TYPES.UPDATE_DIGITAL_EVENT) {
        /**
         * Update digital event
         */
        dispatch("event/updateDigitalEvent", { includeChildren: 1 }, { root: true });
      } else if (message.messageType === WSS_MESSAGE_TYPES.UPDATE_DIGITAL_EVENT_ROOM) {
        /**
         * Update event room
         */
        dispatch("room/updateDigitalEventRoom", { uuid: message.messageBody }, { root: true });
      } else if (message.messageType === WSS_MESSAGE_TYPES.ADD_DIGITAL_EVENT_TABLE) {
        /**
         * Add new event table
         */
        dispatch("tables/addTable", { tableUUID: message.messageBody }, { root: true });
      } else if (message.messageType === WSS_MESSAGE_TYPES.UPDATE_DIGITAL_EVENT_TABLE) {
        /**
         * Update event table
         */
        dispatch("tables/refreshTable", { tableUUID: message.messageBody }, { root: true });
      } else if (message.messageType === WSS_MESSAGE_TYPES.DELETE_DIGITAL_EVENT_TABLE) {
        /**
         * Delete event table
         */
        commit("deleteTable", { tableUUID: message.messageBody }, { root: true });
      } else if (message.messageType === WSS_MESSAGE_TYPES.MERGE_ASSIGNMENTS) {
        /**
         * Merge assignments
         */
        dispatch("room/mergeAssignments");
      } else if (message.messageType === WSS_MESSAGE_TYPES.ANNOUNCE_MESSAGE) {
        /**
         * Announce message
         */
        if (message.messageStatus === MESSAGE_STATUS.PUBLISHED) {
          commit("showAnnounce", { message: message });
        } else if (message.messageStatus === MESSAGE_STATUS.DRAFT) {
          commit("saveAnnounce", { message: message });
        } else if (message.messageStatus === MESSAGE_STATUS.DELETED) {
          commit("deleteAnnounce", { message: message });
        }
      } else if (message.messageType === WSS_MESSAGE_TYPES.BANNER_MESSAGE) {
        /**
         * Banner message
         */
        if (message.messageStatus === MESSAGE_STATUS.PUBLISHED) {
          commit("showAnnounce", { message: message, announceType: ANNOUNCEMENT_TYPES.CELEBRATION });
        } else if (message.messageStatus === MESSAGE_STATUS.DRAFT) {
          commit("saveAnnounce", { message: message });
        } else if (message.messageStatus === MESSAGE_STATUS.DELETED) {
          commit("deleteAnnounce", { message: message });
        }
      } else if (message.messageType === WSS_MESSAGE_TYPES.ROOM_MESSAGE) {
        /**
         * Room message
         */
        if (message.updatedAt !== "") {
          commit("chat/updateRoomMessage", message, { root: true });
        } else {
          commit("addToRoomMessages", { groupUUID: roomUUID, message: message }, { root: true });
        }
      } else if (message.messageType === WSS_MESSAGE_TYPES.TABLE_MESSAGE) {
        /**
         * Table message
         */
        commit(
          "addToTableMessages",
          {
            groupUUID: tableUUID,
            message: message
          },
          { root: true }
        );
      } else if (message.messageType === WSS_MESSAGE_TYPES.PRIVATE_MESSAGE) {
        /**
         * Private message
         */
        commit("addToPrivateMessages", { message: message }, { root: true });
        commit(
          "chat/addUserPrivateUser",
          {
            userUUID: message.userUUID,
            firstName: message.meta.userMeta.firstName,
            lastName: message.meta.userMeta.lastName,
            domain: message.meta.userMeta.meta.domain
          },
          { root: true }
        );
      } else if (message.messageType === WSS_MESSAGE_TYPES.INVITE) {
        /**
         * Invites
         */
        if (message.inviteStatus === INVITE_STATUSES.SEND) {
          dispatch("invites/processReceivedInvite", message, { root: true });
        } else if (message.inviteStatus === INVITE_STATUSES.ACCEPT) {
          dispatch("invites/processAcceptedInvite", message, { root: true });
        } else {
          dispatch("invites/processDeclinedInvite", message, { root: true });
        }
      } else if (message.messageType === WSS_MESSAGE_TYPES.PHOTOWALL_CREATE_POST) {
        /**
         * Photo wall create new post
         */
        if (message.messageBody.roomUUID !== roomUUID) return;

        commit(
          "photoWall/addGalleryPost",
          {
            post: message.messageBody
          },
          {
            root: true
          }
        );
      } else if (message.messageType === WSS_MESSAGE_TYPES.PHOTOWALL_UPDATE_POST) {
        /**
         * Photo wall update post
         */
        commit(
          "photoWall/updateGalleryPost",
          {
            post: message.messageBody
          },
          {
            root: true
          }
        );
      } else if (message.messageType === WSS_MESSAGE_TYPES.PHOTOWALL_DELETE_POST) {
        /**
         * Photo wall delete post
         */
        commit("photoWall/deleteGalleryPost", message.messageBody.uuid, { root: true });
      }
    });

    commit("setWebSocket", { socket: socket });
  },

  /**
   * Close WebSocket connection
   * @param state
   * @param commit
   */
  disconnect({ state, commit }) {
    state.socket.disconnect();
    commit("setWebSocket", { socket: null });
  },

  /**
   * Send message via WebSocket connection
   * @param state
   * @param payload {Object}
   */
  sendToSocketUpdate({ state }, payload) {
    payload.action = "onMessageUpdate";

    logger(":>>", payload);

    state.socket.send(JSON.stringify(payload));
  },

  /**
   * Send message via WebSocket connection
   * @param state
   * @param commit
   * @param dispatch
   * @param getters
   * @param payload {Object}
   */
  sendToSocket({ state, commit, dispatch, rootState, rootGetters }, payload) {
    payload.action = "onMessage";
    payload.page = getPage();
    payload.userUUID = rootState.users.user.user_uuid;
    payload.messageFrom = rootGetters["users/getUserFullName"](
      rootGetters["users/getUser"](rootState.users.user.user_uuid)
    );
    // createdAt will be set on the server side (in AWS Lambda (messages.py))
    // payload.createdAt = new Date().toISOString();

    logger(":>>", payload);

    if (payload.messageType === WSS_MESSAGE_TYPES.ROOM_MESSAGE) {
      commit(
        "addToRoomMessages",
        {
          groupUUID: payload.groupUUID,
          message: JSON.parse(JSON.stringify(payload))
        },
        { root: true }
      );
    } else if (payload.messageType === WSS_MESSAGE_TYPES.TABLE_MESSAGE) {
      commit(
        "addToTableMessages",
        {
          groupUUID: payload.groupUUID,
          message: JSON.parse(JSON.stringify(payload))
        },
        { root: true }
      );
    } else if (payload.messageType === WSS_MESSAGE_TYPES.PRIVATE_MESSAGE) {
      commit("addToPrivateMessages", { message: JSON.parse(JSON.stringify(payload)) }, { root: true });
    }

    state.socket.send(JSON.stringify(payload));

    // reset pings when we have a message
    dispatch("setPingHandler");
  },

  /**
   * Send "Ping" message
   * @param dispatch
   */
  sendPing({ dispatch }) {
    dispatch("sendToSocket", {
      messageType: WSS_MESSAGE_TYPES.PING,
      messageBody: WSS_MESSAGE_TYPES.PING
    });
  },

  /**
   * Send ping to keep socket open
   * @param state
   * @param dispatch
   * @param commit
   * @param rootGetters
   */
  setPingHandler({ state, dispatch, commit, rootGetters }) {
    if (state.pingInterval) {
      clearInterval(state.pingInterval);
      commit("setPingInterval", { value: null });
    }

    commit("setPingInterval", {
      value: setInterval(() => {
        dispatch("sendPing");
      }, rootGetters["event/webSocketConfig"].pingDelay)
    });
  },

  /**
   * Send message to event
   * @param dispatch
   * @param rootState
   * @param message
   * @param status
   * @param timeout
   * */
  sendAnnounce({ dispatch, rootState }, { message, status = "PUBLISHED", timeout = 0 }) {
    let payload = {
      messageBody: message,
      messageType: WSS_MESSAGE_TYPES.ANNOUNCE_MESSAGE,
      groupUUID: rootState.event.context.EVENT_GLOBALS.EVENT_UUID,
      messageUUID: getUUID(),
      messageStatus: status,
      timeout: timeout
    };

    dispatch("sendToSocket", payload);
  },

  /**
   * Send message to event
   * @param dispatch
   * @param rootState
   * @param message
   * @param status
   * @param timeout
   */
  sendBanner({ dispatch, rootState }, { message, status = "PUBLISHED", timeout = 0 }) {
    let payload = {
      messageBody: message,
      messageType: WSS_MESSAGE_TYPES.BANNER_MESSAGE,
      messageStatus: status,
      messageUUID: getUUID(),
      groupUUID: rootState.event.context.EVENT_GLOBALS.EVENT_UUID,
      timeout: timeout
    };
    dispatch("sendToSocket", payload);
  },

  /**
   * Send message to room
   * @param dispatch
   * @param rootGetters
   * @param message
   */
  sendToRoom({ dispatch, rootGetters }, { message }) {
    let payload = {
      messageBody: message.text,
      messageUUID: getUUID(),
      isFromStream: false,
      messageType: WSS_MESSAGE_TYPES.ROOM_MESSAGE,
      groupUUID: rootGetters["room/activeRoom"].uuid
    };
    dispatch("sendToSocket", payload);
  },

  /**
   * Send message to table
   * @param dispatch
   * @param rootGetters
   * @param message
   */
  sendToTable({ dispatch, rootGetters }, { message }) {
    let payload = {
      messageBody: message,
      messageUUID: getUUID(),
      isFromStream: false,
      messageType: WSS_MESSAGE_TYPES.TABLE_MESSAGE,
      groupUUID: rootGetters["tables/userTable"].uuid
    };

    dispatch("sendToSocket", payload);
  },

  /**
   * Send system message
   * @param dispatch
   * @param payload
   */
  sendSystemMessage({ dispatch }, payload) {
    dispatch("sendToSocket", Object.assign(payload, { messageType: WSS_MESSAGE_TYPES.SYSTEM }));
  },

  /**
   * Send info message
   * @param dispatch
   * @param payload
   */
  sendInfoMessage({ dispatch }, payload) {
    dispatch("sendToSocket", Object.assign(payload, { infoMessage: true }));
  },

  /**
   * Start getting roomState from DynamoDB every 5 sec
   * @param state
   * @param commit
   * @param dispatch
   * @param rootState
   * @param rootGetters
   */
  startStatePulling({ state, commit, dispatch, rootState, rootGetters }) {
    if (state.statePullingInterval) {
      clearInterval(state.statePullingInterval);
      commit("setStatePullingInterval", { value: null });
    }

    dispatch("room/getRoomState", { roomUUID: rootGetters["room/activeRoom"].uuid }, { root: true });

    commit("setStatePullingInterval", {
      value: setInterval(() => {
        dispatch("room/getRoomState", { roomUUID: rootGetters["room/activeRoom"].uuid }, { root: true });
      }, rootState.event.config.restApiConfig.stateUpdateDelay)
    });
  },

  /**
   * Stop getting roomState from DynamoDB every N sec
   * @param state {Object}
   * @param commit {Function}
   */
  stopStatePulling({ state, commit }) {
    if (state.statePullingInterval) {
      clearInterval(state.statePullingInterval);
      commit("setStatePullingInterval", { value: null });
    }
  },

  /**
   * Send Table creation notify to all users in the room
   * @param dispatch {Function}
   * @param rootGetters {Object}
   * @param tableUUID {String}
   */
  tableCreationNotify({ dispatch, rootGetters }, { tableUUID }) {
    let payload = {
      messageBody: tableUUID,
      messageType: WSS_MESSAGE_TYPES.TABLE_CREATION,
      groupUUID: rootGetters["room/activeRoom"].uuid
    };

    dispatch("sendToSocket", payload);
  }
};
